import Phaser from 'phaser';
import type { HouseStageInput, HouseStageView } from './house-stage';
import { getHouseStageView } from './house-stage';

export interface BiomeHandle {
  destroy(): void;
  update(input: HouseStageInput): void;
}

export const HOUSE_ASSET_PATHS = {
  backdrop: '/assets/generated/house/house-backdrop.webp',
  cleanSparkle: '/assets/generated/house/prop-clean-sparkle.png',
  foodBowl: '/assets/generated/house/prop-food-bowl.png',
  litterBox: '/assets/generated/house/prop-litter-box.png',
  medicine: '/assets/generated/house/prop-medicine.png',
  moodHeart: '/assets/generated/house/prop-mood-heart.png',
  poop: '/assets/generated/house/prop-poop.png',
  toys: '/assets/generated/house/prop-toys.png',
  waterBowl: '/assets/generated/house/prop-water-bowl.png',
} as const;

export const HOUSE_TEXTURE_KEYS = {
  backdrop: 'house-backdrop',
  cleanSparkle: 'house-prop-clean-sparkle',
  foodBowl: 'house-prop-food-bowl',
  litterBox: 'house-prop-litter-box',
  medicine: 'house-prop-medicine',
  moodHeart: 'house-prop-mood-heart',
  poop: 'house-prop-poop',
  toys: 'house-prop-toys',
  waterBowl: 'house-prop-water-bowl',
} as const;

interface SceneObjects {
  backdrop: Phaser.GameObjects.Image;
  dim: Phaser.GameObjects.Rectangle;
  foodBowl: Phaser.GameObjects.Image;
  litterBox: Phaser.GameObjects.Image;
  medicine: Phaser.GameObjects.Image;
  moodHeart: Phaser.GameObjects.Image;
  poop: Phaser.GameObjects.Image[];
  stink: Phaser.GameObjects.Graphics[];
  sickSparkle: Phaser.GameObjects.Image;
  toys: Phaser.GameObjects.Image;
  waterBowl: Phaser.GameObjects.Image;
}

export function preloadHouseAssets(scene: Phaser.Scene): void {
  scene.load.image(HOUSE_TEXTURE_KEYS.backdrop, HOUSE_ASSET_PATHS.backdrop);
  scene.load.image(HOUSE_TEXTURE_KEYS.cleanSparkle, HOUSE_ASSET_PATHS.cleanSparkle);
  scene.load.image(HOUSE_TEXTURE_KEYS.foodBowl, HOUSE_ASSET_PATHS.foodBowl);
  scene.load.image(HOUSE_TEXTURE_KEYS.litterBox, HOUSE_ASSET_PATHS.litterBox);
  scene.load.image(HOUSE_TEXTURE_KEYS.medicine, HOUSE_ASSET_PATHS.medicine);
  scene.load.image(HOUSE_TEXTURE_KEYS.moodHeart, HOUSE_ASSET_PATHS.moodHeart);
  scene.load.image(HOUSE_TEXTURE_KEYS.poop, HOUSE_ASSET_PATHS.poop);
  scene.load.image(HOUSE_TEXTURE_KEYS.toys, HOUSE_ASSET_PATHS.toys);
  scene.load.image(HOUSE_TEXTURE_KEYS.waterBowl, HOUSE_ASSET_PATHS.waterBowl);
}

export function buildHouseScene(scene: Phaser.Scene, _opts?: { reducedMotion?: boolean }): BiomeHandle {
  const objects = createSceneObjects(scene);
  let lastInput: HouseStageInput | null = null;

  const layout = (): void => {
    layoutBackdrop(scene, objects.backdrop);
    layoutProps(scene, objects);
    if (lastInput) applyView(scene, objects, getHouseStageView(lastInput));
  };

  const onResize = (): void => layout();
  scene.scale.on('resize', onResize);
  layout();

  return {
    update(input: HouseStageInput): void {
      lastInput = input;
      applyView(scene, objects, getHouseStageView(input));
    },
    destroy(): void {
      scene.scale.off('resize', onResize);
      objects.backdrop.destroy();
      objects.foodBowl.destroy();
      objects.litterBox.destroy();
      objects.waterBowl.destroy();
      objects.toys.destroy();
      objects.medicine.destroy();
      objects.sickSparkle.destroy();
      objects.moodHeart.destroy();
      objects.poop.forEach((poop) => poop.destroy());
      objects.stink.forEach((stink) => stink.destroy());
      objects.dim.destroy();
    },
  };
}

function createSceneObjects(scene: Phaser.Scene): SceneObjects {
  const backdrop = scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.backdrop).setDepth(-40).setOrigin(0.5);
  const foodBowl = scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.foodBowl).setDepth(-5).setOrigin(0.5);
  const waterBowl = scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.waterBowl).setDepth(-5).setOrigin(0.5);
  const toys = scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.toys).setDepth(-5).setOrigin(0.5);
  const litterBox = scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.litterBox).setDepth(20).setOrigin(0.5).setVisible(false);
  const medicine = scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.medicine).setDepth(21).setOrigin(0.5).setVisible(false);
  const sickSparkle = scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.cleanSparkle).setDepth(21).setOrigin(0.5).setVisible(false);
  const moodHeart = scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.moodHeart).setDepth(21).setOrigin(0.5).setVisible(false);
  const poop = Array.from({ length: 5 }, () =>
    scene.add.image(0, 0, HOUSE_TEXTURE_KEYS.poop).setDepth(21).setOrigin(0.5).setVisible(false),
  );
  const stink = Array.from({ length: 5 }, () => scene.add.graphics().setDepth(22).setVisible(false));
  const dim = scene.add.rectangle(0, 0, 1, 1, 0x101422, 0).setDepth(80).setOrigin(0).setVisible(false);

  return {
    backdrop,
    dim,
    foodBowl,
    litterBox,
    medicine,
    moodHeart,
    poop,
    stink,
    sickSparkle,
    toys,
    waterBowl,
  };
}

function layoutBackdrop(scene: Phaser.Scene, image: Phaser.GameObjects.Image): void {
  const w = scene.scale.width;
  const h = scene.scale.height;
  const texture = image.texture.getSourceImage() as { width: number; height: number };
  const scale = Math.max(w / texture.width, h / texture.height);
  image.setPosition(w / 2, h / 2).setScale(scale);
}

function layoutProps(scene: Phaser.Scene, objects: SceneObjects): void {
  const w = scene.scale.width;
  const h = scene.scale.height;
  const floorY = h * 0.79;
  const propScale = Math.max(0.74, Math.min(1.05, w / 1920));

  objects.foodBowl.setPosition(w * 0.18, floorY).setScale(propScale * 0.9);
  objects.waterBowl.setPosition(w * 0.25, floorY + 4).setScale(propScale * 0.86);
  objects.toys.setPosition(w * 0.76, floorY + 10).setScale(propScale * 0.84);
  objects.medicine.setPosition(w * 0.28, h * 0.56).setScale(propScale * 0.72);
  objects.sickSparkle.setPosition(w * 0.33, h * 0.53).setScale(propScale * 0.72);
  objects.moodHeart.setPosition(w * 0.67, h * 0.55).setScale(propScale * 0.72);
  objects.dim.setSize(w, h);

  const litterX = w * 0.87;
  const litterY = floorY + propScale * 30;
  objects.litterBox.setPosition(litterX, litterY).setScale(propScale * 0.34);

  layoutWaste(scene, objects, 0);
}

function applyView(scene: Phaser.Scene, objects: SceneObjects, view: HouseStageView): void {
  const visiblePoops = view.poopMarkers;
  objects.backdrop.setTint(view.backdropTint);
  objects.foodBowl.setTint(view.propTint);
  objects.litterBox.setTint(view.propTint);
  objects.medicine.setTint(view.propTint);
  objects.moodHeart.setTint(view.propTint);
  objects.poop.forEach((poop) => poop.setTint(view.propTint));
  objects.sickSparkle.setTint(view.propTint);
  objects.toys.setTint(view.propTint);
  objects.waterBowl.setTint(view.propTint);

  objects.medicine.setVisible(view.showSickAccent);
  objects.sickSparkle.setVisible(view.showSickAccent);
  objects.moodHeart.setVisible(view.mood === 'sad' || view.showTantrumMarks);
  objects.moodHeart.setAlpha(view.showTantrumMarks ? 1 : 0.52);

  objects.dim.setVisible(view.dimAlpha > 0);
  objects.dim.setAlpha(view.dimAlpha);

  layoutProps(scene, objects);
  layoutWaste(scene, objects, visiblePoops);
}

function layoutWaste(scene: Phaser.Scene, objects: SceneObjects, visiblePoops: number): void {
  const w = scene.scale.width;
  const h = scene.scale.height;
  const floorY = h * 0.79;
  const propScale = Math.max(0.74, Math.min(1.05, w / 1920));
  const litterX = w * 0.87;
  const litterY = floorY + propScale * 30;
  const layouts: Array<Array<[number, number]>> = [
    [],
    [[0, -12]],
    [[-18, -10], [18, -8]],
    [[-26, -7], [0, -15], [26, -7]],
    [[-30, -5], [-10, -15], [12, -13], [32, -4]],
    [[-36, -4], [-18, -15], [2, -10], [22, -16], [38, -5]],
  ];
  const positions = layouts[Math.max(0, Math.min(5, visiblePoops))]!;

  objects.litterBox.setVisible(true);
  objects.poop.forEach((poop, i) => {
    const position = positions[i];
    if (!position) {
      poop.setVisible(false);
      objects.stink[i]?.setVisible(false);
      return;
    }
    const x = litterX + position[0] * propScale;
    const y = litterY + position[1] * propScale;
    poop.setPosition(x, y).setScale(propScale * 0.44).setVisible(true);
    drawStink(objects.stink[i]!, x, y, propScale);
    objects.stink[i]!.setVisible(true);
  });
}

function drawStink(g: Phaser.GameObjects.Graphics, x: number, y: number, scale: number): void {
  g.clear();
  g.lineStyle(Math.max(1, 2 * scale), 0x82d66f, 0.55);
  for (let i = 0; i < 2; i++) {
    const offset = (i - 0.5) * 10 * scale;
    g.beginPath();
    g.moveTo(x + offset, y - 14 * scale);
    g.lineTo(x + offset + 4 * scale, y - 20 * scale);
    g.lineTo(x + offset - 1 * scale, y - 27 * scale);
    g.strokePath();
  }
}
